import { useAccount, usePublicClient, useWalletClient } from "wagmi"
import { Address } from "viem"
import { CommandBuilder, SwapRouter02Encoder } from "doppler-router"
import { signPermit } from "doppler-router/dist/Permit2"
import {
  CHAIN_ID,
  permit2Abi,
  TOKEN_ADDRESSES,
  universalRouterAbi,
} from "./constants"
import { getDrift } from "@/lib/utils/coins"
import { ReadQuoter, DOPPLER_V3_ADDRESSES } from "doppler-v3-sdk"
import { toast } from "sonner"

type TradeType = "buy" | "sell"

/**
 * This hook returns a tradeCoins function that allows to buy and sell tokens. It uses two functions:
 *   - swapCoins. The main function that uses doppler v3 to swap coins. The values change if buying or selling.
 *   - tradeCoins. A function that verifies that the values sent are correct and shows a toast when the transaction is sent.
 * @param tokenAddress The ERC20 adress of the coin
 * @returns tradeCoins function with params:
 *      type: TradeType
 *      amount: bigint
 */
export function useTradeCoins(tokenAddress: Address) {
  const account = useAccount()
  const { data: walletClient } = useWalletClient(account)
  const publicClient = usePublicClient({ chainId: CHAIN_ID })

  async function swapCoins(
    isBuying: boolean,
    tokens: Address[],
    amount: bigint
  ) {
    if (!walletClient || !account.address)
      throw new Error("Missing values for swap")

    let permit
    let signature
    if (!isBuying) {
      const [block, allowance] = await Promise.all([
        publicClient.getBlock(),
        publicClient.readContract({
          address: DOPPLER_V3_ADDRESSES[CHAIN_ID].permit2,
          abi: permit2Abi,
          functionName: "allowance",
          args: [
            account.address,
            tokens[0],
            DOPPLER_V3_ADDRESSES[CHAIN_ID].universalRouter,
          ],
        }),
      ])
      permit = {
        details: {
          token: tokens[0],
          amount,
          expiration: 0n,
          nonce: BigInt(allowance[2]),
        },
        spender: DOPPLER_V3_ADDRESSES[CHAIN_ID].universalRouter,
        sigDeadline: block.timestamp + 3600n,
      }
      signature = await signPermit(
        permit,
        walletClient,
        CHAIN_ID,
        DOPPLER_V3_ADDRESSES[CHAIN_ID].permit2
      )
    }

    const path = new SwapRouter02Encoder().encodePath(tokens, 10000)
    const commandBuilder = new CommandBuilder()
    if (isBuying) {
      commandBuilder
        .addWrapEth(DOPPLER_V3_ADDRESSES[CHAIN_ID].universalRouter, amount)
        .addV3SwapExactIn(account.address, amount, 0n, path, false)
    } else {
      commandBuilder
        .addPermit2Permit(permit!, signature!)
        .addV3SwapExactIn(account.address, amount, 0n, path, true)
    }
    const [commands, inputs] = commandBuilder.build()

    const txHash = await walletClient.writeContract({
      address: DOPPLER_V3_ADDRESSES[CHAIN_ID].universalRouter,
      abi: universalRouterAbi,
      functionName: "execute",
      args: [commands, inputs],
      value: isBuying ? amount : undefined,
    })
    const toastId = toast.loading("Processing your transaction")
    const receipt = await publicClient.waitForTransactionReceipt({
      hash: txHash,
    })

    if (receipt.status === "reverted") throw toastId
    return { txHash, toastId }
  }

  async function tradeCoins(type: TradeType, amount: bigint) {
    if (!account.address || !publicClient)
      throw new Error("Missing values for support")

    if (account.chainId !== CHAIN_ID) throw new Error("Wrong chain")

    const isBuying = type === "buy"
    const drift = getDrift(CHAIN_ID, walletClient)
    const quoter = new ReadQuoter(
      DOPPLER_V3_ADDRESSES[CHAIN_ID].quoterV2,
      DOPPLER_V3_ADDRESSES[CHAIN_ID].univ2Router02,
      // @ts-ignore
      drift
    )

    const tokens = isBuying
      ? [TOKEN_ADDRESSES[0] as Address, tokenAddress]
      : [tokenAddress, TOKEN_ADDRESSES[0] as Address]

    const quote = await quoter.quoteExactInputV3({
      tokenIn: tokens[0],
      tokenOut: tokens[1],
      amountIn: amount,
      fee: 10000,
      sqrtPriceLimitX96: 0n,
    })
    const balance = await publicClient.getBalance({ address: account.address })
    const total = isBuying
      ? amount + 2n * quote.gasEstimate
      : 2n * quote.gasEstimate

    if (balance < total)
      throw new Error("Not enough balance for this transaction")

    const swap = await swapCoins(isBuying, tokens, amount)

    return { ...swap, amountOut: quote.amountOut }
  }

  return tradeCoins
}
